import React from "react";
import DatePicker from "components/DatePicker";
import CarCompanySelector from "./CarCompanySelector";
import FloatingField from "./FloatingField";
import { ACCIDENT_INSURANCE_OPTIONS } from "../constants";

export default function CarInsurancePanel({
  companies,
  selectedCompanyCode,
  onSelectCompany,
  seats,
  onSeatsChange,
  onSeatsBlur,
  seatsError,
  dateEffect,
  onDateChange,
  minDateEffect,
  maxDateEffect,
  accidentInsurance,
  onAccidentInsuranceChange,
  onOpenBenefitSheet,
}) {
  const isAccidentActive = Boolean(
    accidentInsurance && accidentInsurance.code === "10"
  );
  const [isExpanded, setIsExpanded] = React.useState(isAccidentActive);

  // Sync isExpanded whenever isAccidentActive changes
  React.useEffect(() => {
    setIsExpanded(isAccidentActive);
  }, [isAccidentActive]);

  const handleToggleAccident = (e) => {
    const isChecked = e.target.checked;
    setIsExpanded(isChecked);
    if (isChecked) {
      const opt10 =
        ACCIDENT_INSURANCE_OPTIONS.find((o) => o.code === "10") || {
          code: "10",
          name: "10.000.000 VNĐ/người/vụ",
          nameWithType: "10.000.000 VNĐ/người/vụ",
        };
      onAccidentInsuranceChange && onAccidentInsuranceChange(opt10);
    } else {
      const opt0 =
        ACCIDENT_INSURANCE_OPTIONS.find((o) => o.code === "0") || {
          code: "0",
          name: "Không",
          nameWithType: "Không",
        };
      onAccidentInsuranceChange && onAccidentInsuranceChange(opt0);
    }
  };

  return (
    <div className="car-insurance__panel-card">
      <h2 className="car-insurance__section-title">
        Thông tin &amp; Gói bảo hiểm
      </h2>

      {/* 1. Quyền lợi nổi bật */}
      <div
        className="car-insurance__benefit-preview"
        onClick={() => onOpenBenefitSheet && onOpenBenefitSheet("detail")}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpenBenefitSheet && onOpenBenefitSheet("detail");
          }
        }}
      >
        <div className="car-insurance__benefit-preview-head">
          <span className="car-insurance__benefit-preview-title">
            Quyền lợi nổi bật
          </span>
          <span className="car-insurance__benefit-preview-info" aria-hidden>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="10" r="9" stroke="#006dff" strokeWidth="1.5" />
              <path d="M10 9v5" stroke="#006dff" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="10" cy="6.5" r="0.75" fill="#006dff" />
            </svg>
          </span>
        </div>
        <ul className="car-insurance__benefit-list">
          <li className="car-insurance__benefit-item">
            <span className="car-insurance__benefit-check" aria-hidden>✓</span>
            <span className="car-insurance__benefit-item-text">
              Về người: <strong>150.000.000đ/người/vụ</strong>
            </span>
          </li>
          <li className="car-insurance__benefit-item">
            <span className="car-insurance__benefit-check" aria-hidden>✓</span>
            <span className="car-insurance__benefit-item-text">
              Về tài sản: <strong>100.000.000đ/vụ</strong>
            </span>
          </li>
          <li className="car-insurance__benefit-item">
            <span className="car-insurance__benefit-check" aria-hidden>✓</span>
            <span className="car-insurance__benefit-item-text">Cấp chứng nhận điện tử nhanh chóng, hợp lệ 100% khi lưu thông</span>
          </li>
        </ul>
      </div>

      <div className="car-insurance__panel-divider" />

      {/* 2. Chọn công ty bảo hiểm */}
      <CarCompanySelector
        companies={companies}
        selectedCompanyCode={selectedCompanyCode}
        onSelectCompany={onSelectCompany}
      />

      <div className="car-insurance__panel-divider" />

      {/* 3. Số chỗ ngồi & Ngày hiệu lực */}
      <div className="car-insurance__panel-row-fields">
        <div className="car-insurance__panel-field-half">
          <FloatingField
            id="car-seats-input"
            label="Số chỗ ngồi"
            type="number"
            inputMode="numeric"
            value={seats}
            onChange={(e) => onSeatsChange(e.target.value)}
            onBlur={onSeatsBlur}
            error={seatsError}
            placeholder="Mặc định: 5"
            isNotMargin
          />
        </div>

        <div className="car-insurance__panel-field-half">
          <div className="car-insurance__panel-date">
            <DatePicker
              error={false}
              title="Ngày hiệu lực"
              placeholder="Ngày hiệu lực *"
              minDate={minDateEffect}
              maxDate={maxDateEffect}
              date={dateEffect}
              defaultDate={minDateEffect}
              onChange={onDateChange}
            />
          </div>
        </div>
      </div>

      {/* 4. Bảo hiểm tai nạn người ngồi trên xe (Toggle Switch & Quyền lợi) */}
      <div
        className={`car-insurance__accident-card ${
          isAccidentActive ? "car-insurance__accident-card--active" : ""
        }`}
      >
        <div className="car-insurance__accident-header">
          <div className="car-insurance__accident-title-group">
            <div className="car-insurance__accident-icon-wrap" aria-hidden>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2L3 6V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V6L12 2Z"
                  fill={isAccidentActive ? "#003bde" : "#e2e8f0"}
                  fillOpacity={isAccidentActive ? "0.15" : "0.5"}
                  stroke={isAccidentActive ? "#003bde" : "#64748b"}
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12L11 14L15 10"
                  stroke={isAccidentActive ? "#003bde" : "#64748b"}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="car-insurance__accident-title-content">
              <label
                htmlFor="accident-insurance-switch"
                className="car-insurance__accident-title"
              >
                Bảo hiểm tai nạn người ngồi trên xe
              </label>
              <div className="car-insurance__accident-badge">
                {isAccidentActive ? (
                  <span className="car-insurance__accident-badge-text car-insurance__accident-badge-text--active">
                    Mức bảo vệ: <strong>10.000.000đ/người/vụ</strong>
                  </span>
                ) : (
                  <span className="car-insurance__accident-badge-text">
                    Mức bảo vệ: 10.000.000đ/người/vụ
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Toggle Switch */}
          <label
            className="car-insurance__switch"
            htmlFor="accident-insurance-switch"
          >
            <input
              id="accident-insurance-switch"
              type="checkbox"
              className="car-insurance__switch-input"
              checked={isAccidentActive}
              onChange={handleToggleAccident}
            />
            <span className="car-insurance__switch-slider" />
          </label>
        </div>

        {/* Thông tin quyền lợi BH tai nạn */}
        <div className="car-insurance__accident-info-box">
          <div
            className="car-insurance__accident-info-head"
            onClick={() => setIsExpanded((prev) => !prev)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsExpanded((prev) => !prev);
              }
            }}
          >
            <div className="car-insurance__accident-info-head-left">
              <svg
                width="14"
                height="14"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="car-insurance__accident-info-head-icon"
              >
                <circle cx="10" cy="10" r="9" stroke="#003bde" strokeWidth="1.5" />
                <path d="M10 9v5" stroke="#003bde" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="10" cy="6.5" r="0.75" fill="#003bde" />
              </svg>
              <span className="car-insurance__accident-info-title">
                Quyền lợi BH tai nạn bao gồm:
              </span>
            </div>
            <button
              type="button"
              className="car-insurance__accident-collapse-btn"
              aria-expanded={isExpanded}
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded((prev) => !prev);
              }}
            >
              <span>{isExpanded ? "Thu gọn" : "Xem chi tiết"}</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="#003bde"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div
            className={`car-insurance__accident-benefit-list ${
              !isExpanded ? "car-insurance__accident-benefit-list--collapsed" : ""
            }`}
          >
            <div className="car-insurance__accident-benefit-item">
              <span className="car-insurance__accident-bullet" aria-hidden>+</span>
              <div className="car-insurance__accident-item-content">
                <strong className="car-insurance__accident-item-label">
                  Phạm vi chi trả{isExpanded ? ":" : ""}
                </strong>
                {isExpanded ? (
                  <span className="car-insurance__accident-item-desc">
                    {" "}Thiệt hại về thân thể hoặc tính mạng xảy ra khi đang ở trên xe,
                    hoặc lúc lên xuống xe trong quá trình tham gia giao thông
                  </span>
                ) : null}
              </div>
            </div>

            <div className="car-insurance__accident-benefit-item">
              <span className="car-insurance__accident-bullet" aria-hidden>+</span>
              <div className="car-insurance__accident-item-content">
                <strong className="car-insurance__accident-item-label">
                  Mức bồi thường{isExpanded ? ":" : ""}
                </strong>
                {isExpanded ? (
                  <span className="car-insurance__accident-item-desc">
                    {" "}Chi trả 100% số tiền bảo hiểm nếu tử vong hoặc thương tật toàn bộ
                    vĩnh viễn. Chi trả theo tỷ lệ phần trăm thương tật quy định nếu bị
                    thương tật bộ phận. Hỗ trợ chi phí y tế điều trị thực tế tùy theo
                    quy tắc của từng hãng bảo hiểm
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
